// UI 用 camelCase，服务器同步协议用 snake_case。本文件双向映射两边。
// 原则：只映射与服务端 schema 对应的字段；UI 专用字段（如 tagIds）不参与 task op。

import { recurrenceSchema, taskPrioritySchema, taskStatusSchema } from '@doneline/shared';
import type {
  Comment,
  Priority,
  Project,
  Recurrence,
  Section,
  Tag,
  Task,
  TaskStatus,
} from '../types';

// A pulled row is untyped server JSON. A malformed enum (priority: 99, status: 5)
// or a broken recurrence object would otherwise pass straight through an `as`
// cast and corrupt the store — breaking priority colors, sort order, or
// formatRecurrence(). Validate the risky fields and fall back to a safe default
// per-field (dropping one bad field beats dropping the whole task).
function toPriority(v: unknown): Priority {
  const r = taskPrioritySchema.safeParse(v);
  return r.success ? (r.data as Priority) : 0;
}

// date-only 列（due_date/start_date/end_date 是 Pg `date`）经 postgres.js → Date → pull 的
// normalizeRow toISOString() 会变成 "2026-06-14T00:00:00.000Z"。客户端要的是 "2026-06-14"，
// 否则 UTC 以西用户 parseISO 会把它算成前一天（分组/逾期/recurrence 全部错一天）。截到 10 位。
function dateOnly(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v.slice(0, 10) : null;
}

function toStatus(v: unknown): TaskStatus {
  const r = taskStatusSchema.safeParse(v);
  return r.success ? (r.data as TaskStatus) : 0;
}

function toRecurrence(v: unknown): Recurrence | null {
  if (v == null) return null;
  const r = recurrenceSchema.safeParse(v);
  return r.success ? (r.data as Recurrence) : null;
}

// ---------- Task ----------

type TaskSyncFields = Partial<{
  project_id: string | null;
  section_id: string | null;
  parent_task_id: string | null;
  title: string;
  description: string | null;
  priority: number;
  status: number;
  due_date: string | null;
  due_at: string | null;
  start_date: string | null;
  end_date: string | null;
  completed_at: string | null;
  recurrence: Recurrence | null;
  sort_key: string;
}>;

export function taskToSyncFields(t: Partial<Task>): TaskSyncFields {
  const out: TaskSyncFields = {};
  if ('projectId' in t) out.project_id = t.projectId ?? null;
  if ('sectionId' in t) out.section_id = t.sectionId ?? null;
  if ('parentTaskId' in t) out.parent_task_id = t.parentTaskId ?? null;
  if ('title' in t && t.title !== undefined) out.title = t.title;
  if ('description' in t) out.description = t.description ?? null;
  if ('priority' in t && t.priority !== undefined) out.priority = t.priority;
  if ('status' in t && t.status !== undefined) out.status = t.status;
  if ('dueDate' in t) out.due_date = t.dueDate ?? null;
  if ('dueAt' in t) out.due_at = t.dueAt ?? null;
  if ('startDate' in t) out.start_date = t.startDate ?? null;
  if ('endDate' in t) out.end_date = t.endDate ?? null;
  if ('completedAt' in t) out.completed_at = t.completedAt ?? null;
  if ('recurrence' in t) out.recurrence = t.recurrence ?? null;
  if ('sortKey' in t && t.sortKey !== undefined) out.sort_key = t.sortKey;
  return out;
}

// tagIds 故意不返回（与 mobile 对齐）：它由独立的 task_tag 同步路径在 _applyRemote 里维护。
// 若这里补上 tagIds: []，mergeList 的 {...prev,...row} 会用空数组清掉本地标签，_applyRemote
// 就得为每次 pull 做整份 tasks 的快照还原（全部新引用 → useShallow 全判变 → 全列重渲染）。
export function syncRowToTask(row: Record<string, unknown>): Task {
  return {
    id: row.id as string,
    projectId: (row.project_id as string | null) ?? null,
    sectionId: (row.section_id as string | null) ?? null,
    parentTaskId: (row.parent_task_id as string | null) ?? null,
    title: (row.title as string) ?? '',
    description: (row.description as string | null) ?? null,
    priority: toPriority(row.priority),
    status: toStatus(row.status),
    dueDate: dateOnly(row.due_date),
    dueAt: (row.due_at as string | null) ?? null,
    startDate: dateOnly(row.start_date),
    endDate: dateOnly(row.end_date),
    completedAt: (row.completed_at as string | null) ?? null,
    recurrence: toRecurrence(row.recurrence),
    sortKey: (row.sort_key as string) ?? '',
    createdAt: (row.created_at as string) ?? new Date().toISOString(),
    updatedAt: (row.updated_at as string) ?? new Date().toISOString(),
  };
}

// ---------- Project ----------

type ProjectSyncFields = Partial<{
  name: string;
  color: string | null;
  icon: string | null;
  description: string | null;
  sort_key: string;
  archived_at: string | null;
}>;

export function projectToSyncFields(p: Partial<Project>): ProjectSyncFields {
  const out: ProjectSyncFields = {};
  if ('name' in p && p.name !== undefined) out.name = p.name;
  if ('color' in p) out.color = p.color ?? null;
  if ('icon' in p) out.icon = p.icon ?? null;
  if ('description' in p) out.description = p.description ?? null;
  if ('sortKey' in p && p.sortKey !== undefined) out.sort_key = p.sortKey;
  if ('archivedAt' in p) out.archived_at = p.archivedAt ?? null;
  return out;
}

export function syncRowToProject(row: Record<string, unknown>): Project {
  return {
    id: row.id as string,
    name: (row.name as string) ?? '',
    color: (row.color as string | null) ?? null,
    icon: (row.icon as string | null) ?? null,
    description: (row.description as string | null) ?? null,
    sortKey: (row.sort_key as string) ?? '',
    archivedAt: (row.archived_at as string | null) ?? null,
    updatedAt: (row.updated_at as string) ?? new Date().toISOString(),
  };
}

// ---------- Section ----------

type SectionSyncFields = Partial<{
  project_id: string;
  name: string;
  sort_key: string;
}>;

export function sectionToSyncFields(s: Partial<Section>): SectionSyncFields {
  const out: SectionSyncFields = {};
  if ('projectId' in s && s.projectId !== undefined) out.project_id = s.projectId;
  if ('name' in s && s.name !== undefined) out.name = s.name;
  if ('sortKey' in s && s.sortKey !== undefined) out.sort_key = s.sortKey;
  return out;
}

export function syncRowToSection(row: Record<string, unknown>): Section {
  return {
    id: row.id as string,
    projectId: row.project_id as string,
    name: (row.name as string) ?? '',
    sortKey: (row.sort_key as string) ?? '',
    updatedAt: (row.updated_at as string) ?? new Date().toISOString(),
  };
}

// ---------- Tag ----------

export function tagToSyncFields(t: Partial<Tag>): Partial<{ name: string; color: string | null }> {
  const out: Partial<{ name: string; color: string | null }> = {};
  if ('name' in t && t.name !== undefined) out.name = t.name;
  if ('color' in t) out.color = t.color ?? null;
  return out;
}

export function syncRowToTag(row: Record<string, unknown>): Tag {
  return {
    id: row.id as string,
    name: (row.name as string) ?? '',
    color: (row.color as string | null) ?? null,
    updatedAt: (row.updated_at as string) ?? new Date().toISOString(),
  };
}

// ---------- Comment ----------

export function commentToSyncFields(
  c: Partial<Comment> & { taskId?: string },
): Partial<{ task_id: string; body: string }> {
  const out: Partial<{ task_id: string; body: string }> = {};
  if ('taskId' in c && c.taskId !== undefined) out.task_id = c.taskId;
  if ('body' in c && c.body !== undefined) out.body = c.body;
  return out;
}

export function syncRowToComment(row: Record<string, unknown>): Comment {
  return {
    id: row.id as string,
    taskId: row.task_id as string,
    body: (row.body as string) ?? '',
    createdAt: (row.created_at as string) ?? new Date().toISOString(),
    updatedAt: row.updated_at as string | undefined,
  };
}
